import styles from './index.module.css'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import api from '@disco/api/server'
import { LOADED, PAUSED, PLAYING } from '@disco/constants/MediaStatus'
import {
  pauseTrack,
  updatePlaybackProgress,
  updatePlaybackStatus,
} from '@disco/state-management/actions/MediaActions'
import {
  deactivateAppliedSegment,
  selectIsSegmentApplied,
  selectSegmentStart,
} from '@disco/state-management/reducers/search-page'
import {
  getTrackPlaybackStatus,
  getTrackPlaying,
} from '@disco/state-management/selectors/media'
import { getMusicProviderFromUrl } from '@disco/utils/links'
import { formatTime } from '@disco/utils/media-player'

import type { SearchContext } from 'types/search-page'

import { getAppleMusicTrackInfo } from 'views/pages/search/utils/apple-music'
import { getSpotifyTrackId } from 'views/pages/search/utils/spotify'
import { useSegmentSelectionEnabled } from 'views/pages/search/utils/use-segment-selection'
import { getYouTubeVideoId } from 'views/pages/search/utils/youtube'

import PlayerButton from 'components/common/player/player-button'
import { Box, Flex, Text } from 'components/ds'
import OptimizedWaveform from 'components/search/search-columns/waveform-column/OptimizedWaveform'
import SegmentSelectorContainer from 'components/search/similarity-player/segment-selector/segment-selector-container'
import Skeleton from 'components/ui/skeleton'

import { useAppleMusicPlayer } from 'hooks/use-apple-music-player'
import { useDiscoPlayer } from 'hooks/use-disco-player'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useSpotifyPlayer } from 'hooks/use-spotify-player'
import { useYouTubePlayer } from 'hooks/use-youtube-player'

// Global state to persist across component unmounts/remounts
let globalPlayerState = {
  stableSearchQuery: '',
}

type Props = {
  context: SearchContext
  height?: number
  searchQuery?: string
}

// Redux track IDs for different services
const REDUX_TRACK_IDS = {
  spotify: 888888,
  youtube: 999999,
  applemusic: 777777,
  disco: 666666,
} as const

const WAVEFORM_HEIGHT = 48
// Tolerance window: currentTime > seekTarget throughout rewind so >= fires immediately; 0.5 s matches coarsest player poll.
const BACKWARD_SEEK_ARRIVED_THRESHOLD_S = 0.5
/**
 * Similarity player component for displaying audio players of similar tracks.
 *
 * Unified component that handles all music service players with shared UI and logic:
 * - YouTube URL → YouTube audio player + waveform
 * - Spotify URL → Spotify embed player + waveform
 * - Apple Music URL → Apple Music preview player + waveform
 *
 * @param context - Search context containing waveform URL and title from API
 * @param searchQuery - The original URL the user searched for
 */
const SimilarityPlayer = ({ context, searchQuery }: Props) => {
  const [waveformData, setWaveformData] = useState<any>(null)
  const [seekTarget, setSeekTarget] = useState<number | null>(null)
  const [audioSeekTime, setAudioSeekTime] = useState<number>(0)
  const [isLoadingWaveform, setIsLoadingWaveform] = useState(false)
  const [waveformError, setWaveformError] = useState<string | null>(null)
  const waveformLoadedRef = useRef<string | null>(null)
  const waveformRetryCountRef = useRef<number>(0)
  const waveformUrlAttemptedRef = useRef<string | null>(null)
  const [contextMatchesQuery, setContextMatchesQuery] = useState(false)
  // Use global state to survive component unmounts/remounts
  const [, forceUpdate] = useState({})
  const urlChangeRef = useRef(false)
  const pendingSeekRef = useRef<number | null>(null)
  const currentTimeRef = useRef<number>(0)

  const dispatch = useDispatch()
  const mainPlayerTrack = useSelector(getTrackPlaying)
  const segmentStart = useSelector(selectSegmentStart)
  const isSegmentEnabled = useSegmentSelectionEnabled()
  const isSegmentApplied = useSelector(selectIsSegmentApplied)
  // Get the main player's playback status to detect when it starts playing
  const mainPlayerStatus = useSelector(state =>
    mainPlayerTrack
      ? getTrackPlaybackStatus(state, { trackId: mainPlayerTrack.id })
      : null,
  )
  // Track previous isPlaying state to detect transitions
  const prevIsPlayingRef = useRef(false)
  const hasEverPlayedRef = useRef(false)
  const lastPlayedStartRef = useRef<number | null>(null)
  const prevMainPlayerPlayingRef = useRef(false)
  // Flag to track if we're programmatically pausing (to avoid Redux side effects)
  const isProgrammaticPauseRef = useRef(false)
  const prevIsSegmentAppliedRef = useRef(false)

  // Track the previous context to detect when a new search was performed
  const prevContextRef = useRef<SearchContext | null>(null)

  // Update global state only when context changes
  useEffect(() => {
    const contextChanged = prevContextRef.current !== context
    prevContextRef.current = context

    if (searchQuery && contextChanged && context) {
      // Check if this is a NEW search vs pagination (context change with same query)
      const isNewSearch = globalPlayerState.stableSearchQuery !== searchQuery

      globalPlayerState.stableSearchQuery = searchQuery

      // Only perform resets for NEW searches, not pagination.
      // This prevents stopping the main player when fetching more results.
      if (isNewSearch) {
        // Flag that we're changing URLs to prevent Redux override during player init
        urlChangeRef.current = true

        // This hides the player UI until a new search is performed
        setContextMatchesQuery(false)
        setWaveformData(null)
        setWaveformError(null)
        setIsLoadingWaveform(false)
        setSeekTarget(null)
        pendingSeekRef.current = null
        hasEverPlayedRef.current = false
        lastPlayedStartRef.current = null
        prevIsSegmentAppliedRef.current = false
        waveformLoadedRef.current = null
        waveformRetryCountRef.current = 0
        waveformUrlAttemptedRef.current = null

        // Reset all SimilarityPlayer IDs only on new search — TRACK_STATUS_UPDATE replaces trackStatus and would wipe the playing track during pagination.
        Object.values(REDUX_TRACK_IDS).forEach(trackId => {
          dispatch(updatePlaybackProgress(trackId, 0))
          dispatch(updatePlaybackStatus(trackId, LOADED, PAUSED))
        })
        // Prevents stale segment seek: SegmentSelectorContainer's resetSegment waits for duration — too late for first play.
        dispatch(deactivateAppliedSegment())

        // Clear the flag after a short delay to allow player initialization
        setTimeout(() => {
          urlChangeRef.current = false
        }, 1000)
      }

      forceUpdate({})
    } else if (!searchQuery && globalPlayerState.stableSearchQuery) {
      // Clear the stable query immediately when search is cleared
      globalPlayerState.stableSearchQuery = ''
      // Reset waveform state immediately
      setWaveformData(null)
      setWaveformError(null)
      setSeekTarget(null)
      pendingSeekRef.current = null
      hasEverPlayedRef.current = false
      lastPlayedStartRef.current = null
      prevIsSegmentAppliedRef.current = false
      waveformLoadedRef.current = null
      setContextMatchesQuery(false)

      // Clear all Redux states
      Object.values(REDUX_TRACK_IDS).forEach(trackId => {
        dispatch(updatePlaybackProgress(trackId, 0))
        dispatch(updatePlaybackStatus(trackId, LOADED, PAUSED))
      })

      forceUpdate({})
    }
  }, [searchQuery, context, dispatch])

  // Derive player type and prepare service-specific data
  const playerType = getMusicProviderFromUrl(
    globalPlayerState.stableSearchQuery,
  )
  const url = globalPlayerState.stableSearchQuery

  // Add cleanup effect to prevent DOM conflicts when component unmounts or player type changes
  useEffect(() => {
    return () => {
      // Cleanup any existing player instances to prevent DOM conflicts
      if (playerType) {
        try {
          // Clean up iframe elements that might be managed by external libraries
          const iframes = document.querySelectorAll(
            'iframe[src*="spotify.com"], iframe[src*="youtube.com"]',
          )
          iframes.forEach(iframe => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe)
            }
          })
        } catch (_e) {
          // Ignore cleanup errors
        }
      }
    }
  }, [playerType])

  // Get service-specific identifiers and hooks

  const spotifyTrackId =
    playerType === 'spotify' ? getSpotifyTrackId(url) : null
  const youtubeVideoId =
    playerType === 'youtube' ? getYouTubeVideoId(url) : null
  const appleMusicTrackInfo =
    playerType === 'applemusic' ? getAppleMusicTrackInfo(url) : null
  const discoLinkInfo = playerType === 'disco' ? context?.disco_link_info : null

  // Initialize appropriate player hook based on type
  const spotifyPlayer = useSpotifyPlayer(spotifyTrackId)
  const youtubePlayer = useYouTubePlayer(youtubeVideoId)
  const appleMusicPlayer = useAppleMusicPlayer(
    appleMusicTrackInfo?.trackId || null,
    appleMusicTrackInfo?.country,
  )

  const discoPlayer = useDiscoPlayer(discoLinkInfo)

  // Select the active player based on type
  const activePlayer =
    playerType === 'spotify'
      ? spotifyPlayer
      : playerType === 'youtube'
      ? youtubePlayer
      : playerType === 'applemusic'
      ? appleMusicPlayer
      : playerType === 'disco'
      ? discoPlayer
      : null

  // Get the Redux track ID for this service (even if no active player)
  const reduxTrackId = playerType
    ? REDUX_TRACK_IDS[playerType]
    : REDUX_TRACK_IDS.spotify

  // Extract player properties with fallbacks
  const {
    duration = 0,
    currentTime = 0,
    isPlaying = false,
    isReady = false,
    error: playerError = null,
    togglePlayPause = () => {},
    seekTo = () => {},
    pause = () => {},
  } = activePlayer || {}

  currentTimeRef.current = currentTime

  // Shared waveform loading logic
  useEffect(() => {
    // Reset retry counter when waveform URL changes
    if (
      waveformLoadedRef.current &&
      waveformLoadedRef.current !== context?.waveform_url
    ) {
      setWaveformData(null)
      setWaveformError(null)
      waveformLoadedRef.current = null
      waveformRetryCountRef.current = 0
      waveformUrlAttemptedRef.current = null
      setContextMatchesQuery(false)
    }
    if (!context?.waveform_url || !url) {
      return
    }

    // Reset retry counter if this is a new URL we haven't attempted
    if (waveformUrlAttemptedRef.current !== context.waveform_url) {
      waveformRetryCountRef.current = 0
      waveformUrlAttemptedRef.current = context.waveform_url
    }

    // Verify context matches current query
    let contextMatches = false
    try {
      const contextUrl = new URL(context.waveform_url)
      const queryParam = contextUrl.searchParams.get('url')
      const identifier =
        playerType === 'spotify'
          ? spotifyTrackId
          : playerType === 'youtube'
          ? youtubeVideoId
          : playerType === 'disco'
          ? discoLinkInfo?.type
          : appleMusicTrackInfo?.trackId?.toString()

      if (!queryParam || !identifier || !queryParam.includes(identifier)) {
        if (contextMatchesQuery) {
          setContextMatchesQuery(false)
        }
        return
      }
      contextMatches = true
    } catch {
      return
    }

    if (contextMatches && !contextMatchesQuery) {
      setContextMatchesQuery(true)
    }

    // Prevent duplicate loading or retrying after max attempts
    if (
      waveformLoadedRef.current === context.waveform_url ||
      isLoadingWaveform
    ) {
      return
    }

    // Stop retrying after 3 attempts
    if (waveformRetryCountRef.current >= 3) {
      return
    }

    const fetchWaveformData = async () => {
      // Capture the URL we're fetching so we can discard the result if the URL
      // changes while the fetch is in-flight (waveformUrlAttemptedRef gets
      // updated by the effect when a new context arrives).
      const capturedUrl = context.waveform_url
      try {
        setIsLoadingWaveform(true)
        setWaveformError(null)
        waveformRetryCountRef.current += 1

        const waveformUrl = new URL(context.waveform_url)
        const apiPath = waveformUrl.pathname + waveformUrl.search
        let data = await api.getUrl(
          apiPath,
          null,
          `${playerType}.getWaveformData`,
        )
        if (playerType === 'disco' && typeof data === 'string') {
          data = JSON.parse(data)
        }

        if (waveformUrlAttemptedRef.current !== capturedUrl) return
        if (data && (data as any).samples) {
          setWaveformData(data)
          waveformLoadedRef.current = capturedUrl
        } else if (Array.isArray(data)) {
          setWaveformData({ samples: data })
          waveformLoadedRef.current = capturedUrl
        } else {
          throw new Error('Invalid waveform data format')
        }
      } catch (err) {
        if (waveformUrlAttemptedRef.current !== capturedUrl) return
        console.error(
          `[SimilarityPlayer] Waveform load error (attempt ${waveformRetryCountRef.current}/3):`,
          err,
        )
        setWaveformError(
          err instanceof Error ? err.message : 'Failed to load waveform',
        )
      } finally {
        setIsLoadingWaveform(false)
      }
    }

    fetchWaveformData()
  }, [
    context?.waveform_url,
    url,
    playerType,
    spotifyTrackId,
    youtubeVideoId,
    discoLinkInfo,
    appleMusicTrackInfo?.trackId,
    isLoadingWaveform,
    contextMatchesQuery,
    context,
  ])

  const handleSegmentPlayback = useCallback(
    (start: number, end: number) => {
      // End handle moved while playing within segment — no need to seek back to start.
      if (
        isPlaying &&
        end > currentTimeRef.current &&
        start === lastPlayedStartRef.current
      ) {
        return
      }
      lastPlayedStartRef.current = start
      if (isPlaying) {
        dispatch(updatePlaybackProgress(reduxTrackId, start))
      } else {
        pendingSeekRef.current = start
      }
      setSeekTarget(start)
      seekTo(start)
    },
    [dispatch, isPlaying, reduxTrackId, seekTo],
  )

  const handlePlayPause = useCallback(() => {
    const audioStart = isSegmentEnabled ? segmentStart : 0
    if (!isPlaying) {
      // pendingSeekRef retains the target if seekTarget clears before play fires (waveform seeks, paused drags).
      const actualTarget =
        pendingSeekRef.current !== null
          ? seekTarget ?? pendingSeekRef.current
          : audioStart
      if (pendingSeekRef.current !== null) {
        seekTo(actualTarget)
        pendingSeekRef.current = null
      } else if (!hasEverPlayedRef.current) {
        seekTo(audioStart)
      }

      lastPlayedStartRef.current = actualTarget
      hasEverPlayedRef.current = true
    }
    togglePlayPause()
  }, [
    isPlaying,
    togglePlayPause,
    seekTarget,
    seekTo,
    segmentStart,
    isSegmentEnabled,
  ])

  // Update Redux when playback position changes
  useEffect(() => {
    if (isReady && !urlChangeRef.current) {
      if (isSegmentApplied && !prevIsSegmentAppliedRef.current) {
        // Snap Redux position on the first segment-applied frame so consumers see the correct start.
        dispatch(updatePlaybackProgress(reduxTrackId, segmentStart))
        prevIsSegmentAppliedRef.current = true
        return
      }
      prevIsSegmentAppliedRef.current = isSegmentApplied
      if (currentTime > 0) {
        dispatch(updatePlaybackProgress(reduxTrackId, currentTime))
      }
    }
  }, [
    dispatch,
    currentTime,
    isReady,
    reduxTrackId,
    segmentStart,
    isSegmentApplied,
  ])

  // Update Redux when play/pause state changes
  useEffect(() => {
    if (isReady && !urlChangeRef.current) {
      // This prevents interfering with the main player's state
      if (!isPlaying && isProgrammaticPauseRef.current) {
        isProgrammaticPauseRef.current = false
        return
      }
      const playbackStatus = isPlaying ? PLAYING : PAUSED
      dispatch(updatePlaybackStatus(reduxTrackId, LOADED, playbackStatus))
    }
  }, [dispatch, isPlaying, isReady, reduxTrackId])

  // Cross-player pause - when SimilarityPlayer starts playing, pause main player
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current
    prevIsPlayingRef.current = isPlaying

    // Detect transition from not playing to playing
    if (!wasPlaying && isPlaying && mainPlayerTrack) {
      dispatch(pauseTrack(mainPlayerTrack.id))
    }
  }, [isPlaying, mainPlayerTrack, dispatch])

  useEffect(() => {
    const mainPlayerIsPlaying = mainPlayerStatus?.playbackStatus === PLAYING
    const wasMainPlayerPlaying = prevMainPlayerPlayingRef.current
    prevMainPlayerPlayingRef.current = mainPlayerIsPlaying

    // Detect transition from not playing to playing in main player
    // Only pause if SimilarityPlayer is currently playing
    if (!wasMainPlayerPlaying && mainPlayerIsPlaying && isPlaying) {
      // Set flag to prevent Redux PAUSED status update from interfering with main player
      isProgrammaticPauseRef.current = true
      pause()
    }
  }, [mainPlayerStatus?.playbackStatus, isPlaying, pause, mainPlayerTrack])

  const handleWaveformSeek = useCallback(
    (seekTime: number) => {
      if (duration > 0) {
        if (isSegmentApplied) {
          dispatch(deactivateAppliedSegment())
        }
        setSeekTarget(seekTime)
        seekTo(seekTime)
        pendingSeekRef.current = isPlaying ? null : seekTime
      }
    },
    [dispatch, duration, isPlaying, isSegmentApplied, seekTo],
  )

  // Hold cursor at seekTarget until audio arrives; direction-aware (backward seeks use BACKWARD_SEEK_ARRIVED_THRESHOLD_S).
  useEffect(() => {
    if (seekTarget !== null) {
      // currentTimeRef is updated every render so this direction check is always fresh.
      const isBackwardSeek = seekTarget < currentTimeRef.current
      const arrived = isBackwardSeek
        ? Math.abs(currentTime - seekTarget) < BACKWARD_SEEK_ARRIVED_THRESHOLD_S
        : currentTime >= seekTarget
      if (arrived) {
        setSeekTarget(null)
        setAudioSeekTime(currentTime)
      } else {
        setAudioSeekTime(seekTarget)
      }
    } else {
      setAudioSeekTime(currentTime)
    }
  }, [seekTarget, currentTime])
  // Memoized truncated waveform data - only recalculate when waveformData changes
  const { processedWaveformData, previewDuration } = React.useMemo(() => {
    if (!waveformData || !waveformData.samples || playerType !== 'spotify') {
      return {
        processedWaveformData: waveformData,
        previewDuration: duration || 180,
      }
    }

    // For Spotify, always use 30 seconds for preview consistency
    const maxPreviewDuration = 30
    // Always assume full song duration when processing (don't use player duration)
    const fullSongDuration = 180

    // Always truncate Spotify tracks to 30 seconds of samples
    const samplesPerSecond = waveformData.samples.length / fullSongDuration
    const maxSamples = Math.floor(samplesPerSecond * maxPreviewDuration)

    // Get the 30-second portion of samples
    const truncatedSamples = waveformData.samples.slice(0, maxSamples)

    // Interpolate/stretch the truncated samples to fill 1800 samples
    const targetSampleCount = 1800
    const stretchedSamples = []

    for (let i = 0; i < targetSampleCount; i++) {
      const sourceIndex =
        (i / targetSampleCount) * (truncatedSamples.length - 1)
      const lowerIndex = Math.floor(sourceIndex)
      const upperIndex = Math.ceil(sourceIndex)
      const fraction = sourceIndex - lowerIndex

      if (upperIndex < truncatedSamples.length) {
        const lowerValue = truncatedSamples[lowerIndex] || 0
        const upperValue = truncatedSamples[upperIndex] || 0
        const interpolatedValue =
          lowerValue + (upperValue - lowerValue) * fraction
        stretchedSamples.push(interpolatedValue)
      } else {
        stretchedSamples.push(truncatedSamples[lowerIndex] || 0)
      }
    }

    return {
      processedWaveformData: {
        ...waveformData,
        samples: stretchedSamples,
      },
      previewDuration: maxPreviewDuration,
    }
  }, [waveformData, playerType, duration])

  // Create track object for waveform with preview duration
  const trackForWaveform = {
    id: reduxTrackId,
    title: context.title,
    duration: previewDuration || duration || 180,
    waveform: null,
    _playbackPosition: currentTime,
  }

  // Show skeleton when not fully ready
  const isInitializing = !activePlayer || !playerType || !contextMatchesQuery
  const showSkeleton =
    isInitializing || (!processedWaveformData && !waveformError)

  return (
    <Flex
      key={`player-${playerType}-${url}`}
      direction="column"
      justify="center"
      width="100%"
      data-testid="similarity-player"
    >
      {/* Conditionally render player containers only when needed */}
      {playerType === 'youtube' && youtubePlayer?.playerId && (
        <Box
          key={youtubePlayer.playerId}
          className={styles.hiddenPlayerOffscreen}
          aria-hidden="true"
        >
          {/* Inner div is the YouTube mount target. YouTube replaces this div
              with an iframe; the outer Box stays React-managed so key changes
              don't try to removeChild a detached node. */}
          <div id={youtubePlayer.playerId} />
        </Box>
      )}
      {playerType === 'spotify' && spotifyPlayer?.playerId && (
        <Box
          key={spotifyPlayer.playerId}
          id={spotifyPlayer.playerId}
          className={styles.hiddenPlayer}
        />
      )}
      {playerType === 'disco' && discoPlayer?.playerId && (
        <Box
          key={discoPlayer.playerId}
          id={discoPlayer.playerId}
          className={styles.hiddenPlayer}
        />
      )}
      <Flex direction="column">
        <Text size="4" weight="bold">
          {context.title}
        </Text>

        {/* Error Display */}
        {(playerError || waveformError) && (
          <Box className={styles.errorBox}>
            <Text size="2" color="red">
              {`${
                playerError || waveformError
              }. Your search results are ready below`}
            </Text>
          </Box>
        )}
        <Flex align="center" gap="3">
          <PlayerButton
            onClick={activePlayer ? handlePlayPause : () => {}}
            type={isPlaying ? 'pause' : 'play'}
          />
          <Box className={styles.waveformContainer}>
            {showSkeleton ? (
              <Flex
                height={`${WAVEFORM_HEIGHT}px`}
                align="center"
                justify="center"
              >
                <Box className={styles.skeletonWrapper}>
                  <Skeleton type="block" lines={1} />
                </Box>
              </Flex>
            ) : processedWaveformData ? (
              <>
                <OptimizedWaveform
                  track={trackForWaveform}
                  parsedWaveformData={processedWaveformData}
                  height={WAVEFORM_HEIGHT}
                  noSeekClick={false}
                  noAnimation={false}
                  isHoverSeekEnabled={false}
                  barWidth={1}
                  barGap={1}
                  onSeek={handleWaveformSeek}
                  externalPlaybackPosition={audioSeekTime}
                />
                <SegmentSelectorContainer
                  url={url}
                  duration={duration}
                  onSegmentPlayback={handleSegmentPlayback}
                />
              </>
            ) : waveformError ? (
              <Flex
                align="center"
                justify="center"
                className={styles.waveformErrorFallback}
              >
                <Text color="gray">No waveform available</Text>
              </Flex>
            ) : null}
          </Box>
          <Text size="2" weight="medium" className={styles.timestamp}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SimilarityPlayer
